import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeftRight } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLoaderData, useParams, useRevalidator } from 'react-router';
import { RHFAutoComplete } from '@/components/auto-complete/RHFAutoComplete';
import { DeviceLoaderResponse } from '../types';
import { stepFields, SwapDeviceFormSchemaType, swapDeviceSchema } from './swap-device-schema';
import { Stepper } from '../deploy-device/Stepper';
import FileUploadStep from './FileUploadStep';
import FieldReportStep from './FieldReportStep';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryProcessObjectsBySiteId } from '../deploy-device/constants';
import { swapDevice, toSwapDeviceMetadata } from './constants';
import { toast } from 'sonner';
import { getUIErrorMessage } from '@/utils/errors';
import { SwapDevicerequest } from './types';

export type CreateProcessObjectFormProps = {
	isOpen: boolean;
	handleToggleForm: () => void;
	isDisabled?: boolean;
};

const steps = [{ name: 'Device details' }, { name: 'Upload file' }, { name: 'Field report' }];

export const SwapDeviceForm = (props: CreateProcessObjectFormProps) => {
	const { isOpen, handleToggleForm, isDisabled = false } = props;
	const { deviceId } = useParams();
	const revalidator = useRevalidator();

	const { devices, antennas, pvPanels, deploymentTypes } = useLoaderData() as DeviceLoaderResponse;

	const deviceToSwap = devices.data.find(device => device.id.toString() === deviceId);

	const [error, setError] = useState('');
	const [currentStep, setCurrentStep] = useState(0);

	const swapDeviceForm = useForm<SwapDeviceFormSchemaType>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		resolver: zodResolver(swapDeviceSchema),
		defaultValues: {
			newDeviceId: [],
			antennaTypeId: [],
			pvPanelTypeId: [],
			upload: {
				processObjectId: '',
				title: '',
				description: '',
				file: undefined
			},
			fieldReport: {
				performedAt: undefined,
				deploymentTypeId: '',
				notes: '',
				hasExternalAntennas: false,
				swapReason: null
			}
		}
	});

	const { isDesktop } = useIsDesktopSize();

	const currentSiteId = deviceToSwap?.siteId?.toString();

	const { data: processObjects } = useQuery({
		queryKey: ['process-objects', currentSiteId],
		queryFn: () => queryProcessObjectsBySiteId(currentSiteId!),
		enabled: !!deviceToSwap?.siteId
	});

	const availableProcessObjects = processObjects?.data || [];

	const { isPending: isSwaping, mutate: mutateSwapDevice } = useMutation({
		mutationFn: (data: SwapDevicerequest) => swapDevice(data, deviceId!),
		mutationKey: ['devices'],
		onSuccess: () => {
			swapDeviceForm.reset();
			toast('Device swapped successfully');
			revalidator.revalidate();
			handleToggleForm();
			setCurrentStep(0);
			setError('');
		},
		onError: error => {
			setError(getUIErrorMessage(error));
		}
	});

	const triggerPoIdError = () => {
		swapDeviceForm.setError('upload.processObjectId', {
			type: 'manual',
			message: 'Process Object ID must be the same in both upload and field report steps.'
		});
		swapDeviceForm.setError('fieldReport.poId', {
			type: 'manual',
			message: 'Process Object ID must be the same in both upload and field report steps.'
		});
	};

	const handleSwapDevice = () => {
		const submitForm = swapDeviceForm.handleSubmit(values => {
			const uploadPoId = values.upload?.processObjectId;
			const fieldReportPoId = values.fieldReport?.poId;

			if (uploadPoId && fieldReportPoId && uploadPoId !== fieldReportPoId) {
				return triggerPoIdError();
			}
			const metadata = toSwapDeviceMetadata(values);
			const jsonString = JSON.stringify(metadata);
			const metaDataFile = new File([jsonString], 'metadata.json', {
				type: 'application/json'
			});

			mutateSwapDevice({ file: values.upload?.file ?? null, metadata: metaDataFile });
		});
		submitForm();
	};

	const handleCloseForm = () => {
		handleToggleForm();
		setError('');
	};

	const handleStepChange = async (newStep: number) => {
		if (newStep > currentStep) {
			const valid = await swapDeviceForm.trigger(stepFields[currentStep]);
			if (!valid) {
				return;
			}
		}
		setCurrentStep(newStep);
	};

	return (
		<Sheet open={isOpen} onOpenChange={handleCloseForm}>
			<SheetTrigger disabled={isDisabled} className={cn({ 'cursor-not-allowed': isDisabled })}>
				<Button
					asChild
					variant="outline"
					onClick={handleToggleForm}
					className="flex justify-start w-fit"
					title={isDisabled ? 'Device is not deployed' : 'Swap device'}
				>
					<span>
						<ArrowLeftRight />
						Swap device
					</span>
				</Button>
			</SheetTrigger>
			<SheetContent
				side={!isDesktop ? 'bottom' : 'right'}
				className={cn('h-full p-5 overflow-auto', { 'sm:max-w-xl ': isDesktop, 'w-full h-5/6': !isDesktop })}
			>
				<SheetHeader>
					<SheetTitle>Swap this device</SheetTitle>
					<SheetDescription>Fill in the details. Click swap when you're done.</SheetDescription>
				</SheetHeader>
				<Stepper
					steps={steps}
					currentStep={currentStep}
					onStepChange={handleStepChange}
					isLoading={isSwaping}
					onSubmit={handleSwapDevice}
					submitBtnLabel="Swap"
				/>
				<Form {...swapDeviceForm}>
					<form className="p-4 flex flex-col gap-3">
						{currentStep === 0 && (
							<div className="w-full flex flex-col gap-3 max-w-4xl">
								<RHFAutoComplete
									control={swapDeviceForm.control}
									closeOnSelect
									name="newDeviceId"
									label="New Device"
									placeholder="Select a device"
									items={devices.data.filter(device => device.site == null)}
									itemToOption={item => ({
										value: item.id.toString(),
										label: item.id.toString()
									})}
								/>

								<RHFAutoComplete
									control={swapDeviceForm.control}
									closeOnSelect
									name="antennaTypeId"
									label="Antenna type"
									placeholder="Select the antenna type"
									items={antennas}
									itemToOption={item => ({ value: item.antennaId.toString(), label: item.model })}
								/>

								<RHFAutoComplete
									control={swapDeviceForm.control}
									closeOnSelect
									name="pvPanelTypeId"
									label="PV panel type"
									placeholder="Select the PV panel type"
									items={pvPanels}
									itemToOption={item => ({ value: item.panelId.toString(), label: item.model })}
								/>
							</div>
						)}

						{currentStep === 1 && (
							<FileUploadStep
								form={swapDeviceForm}
								processObjectIds={availableProcessObjects.map(po => po.id.toString())}
							/>
						)}

						{currentStep === 2 && (
							<FieldReportStep
								form={swapDeviceForm}
								availableProcessObjects={availableProcessObjects}
								deploymentTypes={deploymentTypes}
							/>
						)}
					</form>
				</Form>
				{error && (
					<div className="text-red-500 text-sm text-center">
						<p>{error}</p>
					</div>
				)}
			</SheetContent>
		</Sheet>
	);
};
